import { useEffect, useRef } from 'react';
import type { Key } from 'react-aria';
import type { Modality } from '@react-aria/interactions';
import { setInteractionModality } from '@react-aria/interactions';
import type { ListFilterState } from '../List/types';

export type FocusedItem =
  | Key
  | {
      id: Key;
      scroll: 'immediate' | 'smooth';
    };

/**
 * A hook that supports declaratively settings the focused key in a list,
 * and scrolling to that key.
 *
 * @example
 * const [focusedKey, setFocusedKey] = useState(null);
 *
 * function onClick() {
 *  setFocusedKey(key);
 * }
 *
 * const state = useFilteredListState(props);
 *
 * useSetFocusedKey(focusedKey, state);
 */

export function useSetFocusedKey<T>(
  focusedItem: FocusedItem,
  state: ListFilterState<T>
) {
  const lastFocusedId = useRef<Key>(null);

  useEffect(() => {
    if (!focusedItem) {
      lastFocusedId.current = null;
      return;
    }

    let modality: Modality = 'virtual';
    let focusedId: Key;

    if (typeof focusedItem === 'object') {
      focusedId = focusedItem.id;
      modality = focusedItem.scroll === 'smooth' ? 'virtual' : 'keyboard';
    } else {
      focusedId = focusedItem;
    }

    if (
      lastFocusedId.current !== focusedId &&
      state.collection.getItem(focusedId)
    ) {
      lastFocusedId.current = focusedId;
      setInteractionModality(modality);
      state.selectionManager.setFocusedKey(focusedId);
    }
  });
}
